import { useRef, useState, type DragEvent } from "react";
import type { Board, Me, Trip } from "../../lib/api.ts";
import { groupBoard, type BoardGroupKey, type BoardRow } from "../../lib/board.ts";
import type { UploadProgress } from "../../lib/upload.ts";
import { PHONE, useMediaQuery } from "../../lib/useMedia.ts";
import { useApi } from "../../lib/useApi.ts";
import { Inbox } from "../Inbox.tsx";
import { BoardDetail } from "./BoardDetail.tsx";
import { isUploadable, uploadVersion, type UploadTarget } from "./uploadVersion.ts";

const HEADING: Record<BoardGroupKey, string> = {
  NEEDS_YOU: "Needs you",
  AWAITING: "Awaiting approval",
  IN_PROGRESS: "In progress",
  OTHERS: "Other editors",
  NOT_STARTED: "Not started",
  DONE: "Done",
  DROPPED: "Dropped",
};

/** Finished business folds away; everything else is the day's work. */
const FOLDED: BoardGroupKey[] = ["DONE", "DROPPED"];

const STATUS: Record<string, string> = {
  PLANNED: "Planned",
  EDITING: "Editing",
  IN_REVIEW: "In review",
  CHANGES_REQUESTED: "Changes requested",
  APPROVED: "Approved",
  DELIVERED: "Delivered",
  ABANDONED: "Dropped",
};

const PHASE: Record<UploadProgress["phase"], string> = {
  hashing: "Reading the file",
  preparing: "Preparing",
  uploading: "Uploading",
  completing: "Finishing upload",
  verifying: "Checking the upload",
  publishing: "Publishing",
  done: "Done",
};

const rowKey = (row: BoardRow) =>
  row.kind === "PROJECT" ? `p:${row.project.id}` : `t:${row.task.tagId}`;

/**
 * "reel-v3-final.mp4" becomes "reel-v3-final"; only the last extension goes, and a leading dot is
 * part of the name. (No lookbehind: Safari before 16.4 cannot even parse one.)
 */
const titleFrom = (filename: string) => filename.replace(/^(.+)\.[^./\\]*$/, "$1");

const carriesFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes("Files");

/**
 * The editor's page: every task worth cutting and every project made to answer one, sorted into
 * what to do next, with the open one beside it (or in place of it, on a phone).
 *
 * A drop here means one thing: start a new project from the file. Aiming at a
 * particular row was asking people to hit a target they could not see until they were already
 * over it, so the board takes the drop as a whole and washes the screen while a file is over it.
 * The one exception is the version box in an open project's own pane, where the project is
 * already named on screen: a file dropped there is that project's next version, and the wash says
 * so while the pointer is over it. Starting a project is also a button at the top for anyone not
 * dragging.
 */
export function EditorBoard({ trip, me }: { trip: Trip; me: Me }) {
  const board = useApi<Board>(`/v1/trips/${trip.id}/editor-board`);
  const phone = useMediaQuery(PHONE);
  const [selected, setSelected] = useState<string | null>(null);
  const [busy, setBusy] = useState<{ name: string; phase: UploadProgress["phase"] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Whether a file is over the board right now. One answer, not one per target.
  const [dragging, setDragging] = useState(false);
  // What the open project's version box says a drop will do, while the pointer is over it. The
  // only exception to "a drop starts a new project", and the only piece of state it costs.
  const [aimed, setAimed] = useState<string | null>(null);
  const canUpload = trip.roles.includes("EDITOR");
  const picker = useRef<HTMLInputElement>(null);

  const data = board.data;
  const groups = data ? groupBoard(data, me.user.id) : [];
  const rows = groups.flatMap((g) => g.rows);
  // A task that has just become a project is the same line on the board; keep it open.
  const open =
    (selected !== null &&
      (rows.find((r) => rowKey(r) === selected) ??
        (selected.startsWith("t:")
          ? rows.find((r) => r.kind === "PROJECT" && r.project.tagId === selected.slice(2))
          : undefined))) ||
    null;

  async function drop(e: DragEvent) {
    if (!carriesFiles(e)) return;
    // Always claimed, even when nothing will be done with it: left alone, the browser would
    // navigate to the file and the page, and whatever is in flight, would be gone.
    e.preventDefault();
    setDragging(false);
    setAimed(null);
    if (!canUpload) return;
    const file = e.dataTransfer.files[0];
    if (file) await send(file, (f) => ({ create: { title: titleFrom(f.name) } }));
  }

  /** The one way a file becomes a version, whether it was dropped or chosen. */
  async function send(file: File, target: UploadTarget | ((file: File) => UploadTarget)) {
    if (busy) return;
    setError(null);
    if (!isUploadable(file)) {
      setError("Choose a video or an image file.");
      return;
    }
    setBusy({ name: file.name, phase: "preparing" });
    let made = false;
    try {
      await uploadVersion({
        tripId: trip.id,
        target: typeof target === "function" ? target(file) : target,
        file,
        onProgress: (p) => setBusy({ name: file.name, phase: p.phase }),
        onCreated: () => {
          made = true;
        },
      });
      await board.reload();
    } catch (err) {
      setError((err as Error).message);
      // The server now holds a project with no versions; the board should say so.
      if (made) await board.reload().catch(() => {});
    } finally {
      setBusy(null);
    }
  }

  const hover = (e: DragEvent) => {
    if (!carriesFiles(e)) return;
    e.preventDefault();
    if (!canUpload) return;
    setDragging(true);
  };
  // Moving between children of the board is not leaving it; only a pointer that has gone outside
  // the board entirely puts the wash away.
  const leave = (e: DragEvent) => {
    if (e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget)) return;
    setDragging(false);
    setAimed(null);
  };

  function line(row: BoardRow) {
    const current = open !== null && rowKey(open) === rowKey(row);
    const label = row.kind === "PROJECT" ? row.project.title : row.task.name;
    return (
      <button
        key={rowKey(row)}
        type="button"
        className="board-row"
        aria-current={current ? "true" : undefined}
        onClick={() => setSelected(rowKey(row))}
      >
        <span className="grow">{label}</span>
        {row.kind === "PROJECT" ? (
          <>
            {row.project.current && (
              <span className="small muted">V{row.project.current.versionNumber}</span>
            )}
            <span className="pill">{STATUS[row.project.status] ?? row.project.status}</span>
          </>
        ) : (
          <span className="pill">Not started</span>
        )}
      </button>
    );
  }

  const list = (
    <div className="stack board-list">
      {groups.map((g) => {
        const heading = `${HEADING[g.key]} (${g.rows.length})`;
        const body = <div className="stack">{g.rows.map(line)}</div>;
        return FOLDED.includes(g.key) ? (
          <details key={g.key} className="board-group">
            <summary>
              <span className="board-heading">{heading}</span>
            </summary>
            {body}
          </details>
        ) : (
          <section key={g.key} className="board-group stack">
            <h3 className="board-heading">{heading}</h3>
            {body}
          </section>
        );
      })}
    </div>
  );

  return (
    <div
      className="stack editor-board"
      data-testid="editor-board"
      onDragOver={hover}
      onDragLeave={leave}
      onDrop={(e) => void drop(e)}
    >
      {/* The whole screen answers, rather than a target the size of a row. Laid over everything
          and deaf to the pointer, so the drop still reaches the board underneath it. */}
      {dragging && canUpload && (
        <div className="drop-wash" data-testid="drop-wash" aria-hidden="true">
          <span className="drop-wash-label">{aimed ?? "Drop to start a new project"}</span>
        </div>
      )}
      <Inbox tripId={trip.id} />
      {board.error && !data && (
        <p role="alert" className="error">
          {board.error.message}
        </p>
      )}
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      {busy && (
        <p role="status" className="muted">
          {PHASE[busy.phase]}: {busy.name}
        </p>
      )}
      {canUpload && (
        <div className="row board-start">
          <button
            type="button"
            className="button primary"
            disabled={busy !== null}
            onClick={() => picker.current?.click()}
          >
            Upload as new project
          </button>
          <span className="small muted">or drop a file anywhere on this page</span>
          {/* The button is the control; this is only the file dialogue it opens. */}
          <input
            ref={picker}
            type="file"
            accept="video/*,image/*"
            className="sr"
            tabIndex={-1}
            disabled={busy !== null}
            aria-label="Video or image file for a new project"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (file) void send(file, { create: { title: titleFrom(file.name) } });
            }}
          />
        </div>
      )}
      {!data && !board.error && <p className="muted">Loading…</p>}
      {data && rows.length === 0 && (
        <div className="card stack">
          <p>
            Nothing to cut yet. Tasks show up here when someone asks for a cut, and you can start a
            project of your own by choosing a finished video or image above.
          </p>
        </div>
      )}
      {data && rows.length > 0 && (
        <div className={phone ? "board-panes" : "board-panes two"}>
          {(!phone || !open) && list}
          {open && (
            <div className="board-pane" data-testid="board-pane">
              <BoardDetail
                trip={trip}
                me={me}
                row={open}
                onChanged={board.reload}
                onDropped={() => setDragging(false)}
                onAim={(label) => {
                  setAimed(label);
                  // The box stops the drag before the board hears it, so it raises the wash too.
                  if (label !== null && canUpload) setDragging(true);
                }}
                {...(phone ? { onLeave: () => setSelected(null) } : {})}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
